// src/pages/profile/AssessmentTable.jsx
import React, { useState, useMemo } from "react";
import { FaEye, FaFileDownload, FaSearch } from "react-icons/fa";

const AssessmentTable = ({
  pastAssestmentData,
  viewAssessmentDetails,
  handleDownloadPDF,
  generateCertificate,
  base64Image
}) => {
  const [filterProject, setFilterProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("latest");

  const SCORE_GREEN = 75;
  const SCORE_YELLOW = 50;

  const calculateTotalMarks = (marksPerSection) => {
    return Object.values(marksPerSection).reduce((sum, marks) => sum + marks, 0);
  };

  const getPercentage = (a) => {
    const total = calculateTotalMarks(a.marks_per_section);
    return total ? (a.total_score / total) * 100 : 0;
  };

  const roundToDecimal = (num, decimals = 1) => {
    if (Number.isInteger(num)) return num;
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const visibleAssessments = useMemo(() => {
    let list = [...pastAssestmentData];
    if (filterProject) {
      list = list.filter((a) => a.project_name === filterProject);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(
        (a) =>
          a.question_code?.toLowerCase().includes(lower) ||
          new Date(a.created_at).toLocaleDateString().includes(lower)
      );
    }
    switch (sortBy) {
      case "highest":
        list.sort((a, b) => getPercentage(b) - getPercentage(a));
        break;
      case "oldest":
        list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      default:
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return list;
  }, [pastAssestmentData, filterProject, searchTerm, sortBy]);

  return (
    <>
      
      <div className="flex flex-col md:flex-row items-start gap-4 p-4 bg-purple-50 rounded-lg border border-purple-100 shadow-sm">
        <div className="w-full md:w-auto">
          <label className="text-xs font-medium text-purple-700 mb-1 block">Project</label>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="w-full px-4 py-2 text-sm text-purple-900 bg-white rounded-lg border border-purple-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-300 transition-all"
          >
            <option value="">All Projects</option>
            {[...new Set(pastAssestmentData.map((a) => a.project_name))].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="w-full md:flex-1">
          <label className="text-xs font-medium text-purple-700 mb-1 block">Search date or code</label>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 text-sm" />
            <input
              type="text"
              placeholder="DD/MM/YYYY or code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-9 text-sm text-purple-900 bg-white rounded-lg border border-purple-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-300 transition-all"
            />
          </div>
        </div>

        <div className="w-full md:w-auto">
          <label className="text-xs font-medium text-purple-700 mb-1 block">Sort by</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-4 py-2 text-sm text-purple-900 bg-white rounded-lg border border-purple-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-300 transition-all"
          >
            <option value="latest">Latest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest %</option>
          </select>
        </div>
      </div>

     
      <div className="hidden md:block rounded-lg border border-gray-200 overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <table className="min-w-full text-center">
            <thead className="bg-gradient-to-l from-indigo-950 to-indigo-800 text-white text-sm">
              <tr>
                <th className="py-3 px-4">Assessment</th>
                <th className="py-3 px-4">Taken On</th>
                <th className="py-3 px-4">Total Score</th>
                <th className="py-3 px-4">View Result</th>
                <th className="py-3 px-4">Answer Sheet</th>
                <th className="py-3 px-4">Certificate</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-gray-200">
              {visibleAssessments.map((assessment) => {
                const pct = getPercentage(assessment);
                const color = pct > SCORE_GREEN ? "text-green-600" :
                             pct > SCORE_YELLOW ? "text-yellow-500" : "text-red-600";
                return (
                  <tr key={assessment._id} className="hover:bg-gray-50">
                    <td className="py-4 px-4 font-bold whitespace-nowrap">{assessment.project_name}</td>
                    <td className="py-4 px-4 whitespace-nowrap">{new Date(assessment.created_at).toLocaleDateString()}</td>
                    <td className={`py-4 px-4 whitespace-nowrap ${color}`}>
                      {roundToDecimal(assessment.total_score || 0)}/{calculateTotalMarks(assessment.marks_per_section)}
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => viewAssessmentDetails(assessment._id)}
                        className="text-blue-600 hover:text-blue-800 text-lg"
                        aria-label={`View details for ${assessment.project_name}`}
                      >
                        <FaEye />
                      </button>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => handleDownloadPDF(assessment._id)}
                        className="text-gray-700 hover:text-green-800 text-lg"
                        aria-label={`Download PDF for ${assessment.project_name}`}
                      >
                        <FaFileDownload />
                      </button>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        onClick={() => generateCertificate(assessment)}
                        className="text-gray-700 hover:text-purple-800 text-lg"
                        aria-label={`Download certificate for ${assessment.project_name}`}
                        disabled={!base64Image}
                      >
                        <FaFileDownload />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      
      <div className="md:hidden space-y-4">
        {visibleAssessments.map((assessment) => {
          const pct = getPercentage(assessment);
          const color = pct > SCORE_GREEN ? "text-green-600" :
                       pct > SCORE_YELLOW ? "text-yellow-500" : "text-red-600";
          return (
            <div key={assessment._id} className="border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <p className="text-xs text-gray-500">Assessment</p>
                  <p className="font-bold">{assessment.project_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p>{new Date(assessment.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Score</p>
                  <p className={color}>
                    {roundToDecimal(assessment.total_score || 0)}/{calculateTotalMarks(assessment.marks_per_section)}
                  </p>
                </div>
              </div>

              <div className="flex justify-between border-t pt-3">
                <button
                  onClick={() => viewAssessmentDetails(assessment._id)}
                  className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                  aria-label={`View details for ${assessment.project_name}`}
                >
                  <FaEye className="mr-1" /> View
                </button>
                <button
                  onClick={() => handleDownloadPDF(assessment._id)}
                  className="flex items-center text-gray-700 hover:text-green-800 text-sm"
                  aria-label={`Download PDF for ${assessment.project_name}`}
                >
                  <FaFileDownload className="mr-1" /> PDF
                </button>
                <button
                  onClick={() => generateCertificate(assessment)}
                  className="flex items-center text-gray-700 hover:text-purple-800 text-sm"
                  aria-label={`Download certificate for ${assessment.project_name}`}
                  disabled={!base64Image}
                >
                  <FaFileDownload className="mr-1" /> Certificate
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default AssessmentTable;